/**
 * The form-first wire vocabulary: FORM cards, their answers, and concept cards.
 *
 * Same stance as `wire.ts`: the backend ships these as `JSONString`, so
 * codegen hands us `string` and everything past `JSON.parse` is `unknown` until
 * `parseForm.ts` checks it. These types are where that widening stops.
 */
import type { SelectOption } from "../../wire";

export const FORM_IDS = ["BRIEF", "LOGISTICS"] as const;

export type FormId = (typeof FORM_IDS)[number];

export const FORM_FIELD_KINDS = [
  "product",
  "multi_chips",
  "single_select",
  "text",
  "textarea",
  "money",
  "date",
  "toggle",
] as const;

export type FormFieldKind = (typeof FORM_FIELD_KINDS)[number];

/** "pending" is a select whose options are still being computed; "failed" means they
 * will not come and the person types instead. */
export const OPTIONS_STATUSES = ["ready", "pending", "failed"] as const;

export type OptionsStatus = (typeof OPTIONS_STATUSES)[number];

export type MoneyRange = {
  /** The value of the `rangeBy` field this range applies to. */
  when: string;
  min: string;
  max: string;
  recommended: string;
};

export type FormFieldSpec = {
  /** Stable id: what comes back in the answer and what `userErrors.field` names. */
  key: string;
  kind: FormFieldKind;
  label: string;
  hint: string;
  required: boolean;
  options: SelectOption[];
  optionsStatus: OptionsStatus;
  minSelections: number;
  maxSelections: number | null;
  /** Prefill: text, an ISO date, a money amount, a select value, "true" for a toggle. */
  suggested: string;
  minDate: string;
  maxDate: string;
  /** money, and product (the price of a pasted link): the brand's currency code. */
  currency: string;
  ranges: MoneyRange[];
  /** money: the key of the field whose value picks the range to show. */
  rangeBy: string;
  allowBarter: boolean;
  /** product: the brand whose catalogue the field queries itself. */
  brandId: string;
  width: "full" | "half";
};

/** A FORM question: a whole card of fields, answered at once. */
export type FormCard = {
  formId: FormId;
  /** The session action that submits this card (`submit_brief`, `submit_logistics`…). */
  action: string;
  prompt: string;
  submitLabel: string;
  fields: FormFieldSpec[];
};

/**
 * What one field holds, on the wire and in the client-side draft alike.
 *
 * Deliberately the same shape in both places: the draft persisted to AsyncStorage,
 * the `values` of the submit action and the `values` of a persisted answer are all
 * `FormValues`, so restoring, submitting and re-opening a submitted form never
 * translate between representations.
 */
export type FormValue =
  | string
  | string[]
  | boolean
  | { productId: string }
  | { url: string; price?: string }
  | { amount: string }
  | { barterOnly: true };

export type FormValues = Record<string, FormValue>;

/** A submitted form, as the persisted answer carries it. */
export type FormAnswerData = {
  formId: FormId;
  values: FormValues;
};

export type Concept = {
  id: string;
  title: string;
  hook: string;
  description: string;
  referenceHandle: string;
  format: string;
};

/** A concept as a message of the thread; `stale` and `chosen` live on the message. */
export type ConceptCardData = {
  concept: Concept;
  /** A newer set replaced this one. */
  stale: boolean;
  chosen: boolean;
};

/** Field key -> the message to show next to it. */
export type FormErrors = Record<string, string>;

/** One refusal from the backend: `field` is the key of the field it belongs to. */
export type FormActionError = { field: string | null; message: string };

/** What a form-first action (submit, choose, regenerate) came back with. */
export type FormActionResult =
  { ok: true } | { ok: false; errors: FormActionError[] };

/** What to compute a select's options for: a catalogue product or a link, never both. */
export type RequestFormOptionsInput = { productId: string } | { url: string };
